import { Plus } from 'lucide-react';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { TableSearch } from '../../../../components/form/Fields';
import { usePropertyImagePreview } from '../../../../utils/otherHelopers';
import ConfirmModal from '../../../../components/withModal/ConfirmModal';
import { useToast } from '../../../../context/TostContext';
import { useLoader } from '../../../../context/LoaderContext';
import CustomTable from '../../../../components/table/CustomTable';
import CommonButton from '../../../../components/commonButton';
import { routes } from '../../../../routes/pageRoutes';
import Axios, { authHeaders } from '../../../../utils/axios';
import { formatMomentDate } from '../../../../utils/dateHelper';
import StatusChip from '../../../../components/statusChip/StatusChip';

const PublicProperties = ({ isLand }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { userDetail } = useSelector((state) => state.allUsers);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [propertiesList, setPropertiesList] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const { renderCell: renderImageCell, renderPreviewModal } = usePropertyImagePreview();
    const toast = useToast();
    const { setLoading: setGlobalLoading } = useLoader();
    const [confirmModalConfig, setConfirmModalConfig] = useState({
        isOpen: false,
        title: "",
        message: "",
        onConfirm: () => { },
        confirmText: "Yes",
        cancelText: "No",
    });

    const fetchProperties = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        try {
            const response = await Axios.get(`/broker/properties/public-properties`, {
                params: {
                    page: currentPage,
                    limit: pageSize,
                    search: searchTerm,
                    user_id: id,
                    is_land: isLand ? "true" : "false"
                },
                ...authHeaders()
            });
            const result = response.data?.data;
            setPropertiesList(result?.data || result?.rows || result || []);
            setTotal(response.data?.meta?.total || response.data?.data?.meta?.total || 0);
        } catch (error) {
            console.error(`Failed to fetch user public ${isLand ? 'lands' : 'properties'}:`, error);
        } finally {
            setLoading(false);
        }
    }, [id, currentPage, pageSize, searchTerm, isLand]);

    useEffect(() => {
        fetchProperties();
    }, [fetchProperties]);

    const handleViewProperty = (row) => {
        const detailRoute = isLand ? routes.viewPublicLand : routes.viewPublicProperty;
        navigate(`${detailRoute.replace(":id", row.id)}`, {
            state: {
                id: row.id,
                is_public: true
            }
        });
    };


    const handleEditProperty = (row) => {
        const isLinkedPublic = !!row.property_id;
        const isStandalonePublic = !row.property_id;
        const editId = isLinkedPublic ? row.property_id : row.id;
        const editRoute = isLand ? routes.editPublicLand : routes.editPublicProperty;

        navigate(`${editRoute.replace(":id", editId)}`, {
            state: {
                item: row,
                isEdit: true,
                isPublicProperty: isStandalonePublic,
                fromUserDetail: true,
                isLand: isLand
            }
        });
    };

    const handleTogglePropertyStatus = (row) => {
        const newStatus = row.status === "Open" ? "Close" : "Open";
        setConfirmModalConfig({
            isOpen: true,
            title: `Confirm Status Change`,
            message: `Are you sure you want to change the status of this property to "${newStatus}"?`,
            confirmText: "Yes",
            cancelText: "No",
            onConfirm: async () => {
                setConfirmModalConfig(prev => ({ ...prev, isOpen: false }));
                setGlobalLoading(true);
                try {
                    const response = await Axios.patch(`/broker/properties/public-properties/${row.id}/change-status`, { status: newStatus }, authHeaders());
                    toast.success(response.data?.message || "Status updated successfully.");
                    setPropertiesList(prev => prev.map(item => item.id === row.id ? { ...item, status: newStatus, updated_at: new Date().toISOString() } : item));
                } catch (error) {
                    toast.error(error.response?.data?.message || error.message || "Failed to update status.");
                } finally {
                    setGlobalLoading(false);
                }
            }
        });
    };

    const handleTogglePropertyArchive = (row) => {
        const isArchived = row.is_archived;
        const actionName = isArchived ? "unarchive" : "archive";
        setConfirmModalConfig({
            isOpen: true,
            title: `Confirm ${isArchived ? "Unarchive" : "Archive"}`,
            message: `Are you sure you want to ${actionName} this property?`,
            confirmText: "Yes",
            cancelText: "No",
            onConfirm: async () => {
                setConfirmModalConfig(prev => ({ ...prev, isOpen: false }));
                setGlobalLoading(true);
                try {
                    const response = await Axios.patch(`/broker/properties/public-properties/${row.id}/${actionName}`, {}, authHeaders());
                    toast.success(response.data?.message || `Property successfully ${isArchived ? 'unarchived' : 'archived'}.`);
                    setPropertiesList(prev => prev.map(item => item.id === row.id ? { ...item, is_archived: !isArchived, updated_at: new Date().toISOString() } : item));
                } catch (error) {
                    toast.error(error.response?.data?.message || error.message || `Failed to ${actionName} property.`);
                } finally {
                    setGlobalLoading(false);
                }
            }
        });
    };

    const handleDeleteProperty = (row) => {
        setConfirmModalConfig({
            isOpen: true,
            title: "Confirm Delete",
            message: "Are you sure you want to delete this property? This action cannot be undone.",
            confirmText: "Delete",
            cancelText: "Cancel",
            onConfirm: async () => {
                setConfirmModalConfig(prev => ({ ...prev, isOpen: false }));
                setGlobalLoading(true);
                try {
                    const response = await Axios.delete(`/broker/properties/public-properties/${row.id}`, authHeaders());
                    toast.success(response.data?.message || "Property deleted successfully.");
                    setPropertiesList(prev => prev.filter(item => item.id !== row.id));
                    setTotal(prev => Math.max(0, prev - 1));
                } catch (error) {
                    toast.error(error.response?.data?.message || error.message || "Failed to delete property.");
                } finally {
                    setGlobalLoading(false);
                }
            }
        });
    };

    const actionList = [
        {
            title: 'View',
            handler: handleViewProperty
        },
        {
            title: 'Edit',
            handler: handleEditProperty
        },
        {
            title: (row) => row.status === 'Open' ? 'Close' : 'Open',
            handler: handleTogglePropertyStatus
        },
        {
            title: (row) => row.is_archived ? 'Unarchive' : 'Archive',
            handler: handleTogglePropertyArchive
        },
        {
            title: 'Delete',
            handler: handleDeleteProperty
        }
    ];

    const columns = useMemo(() => {
        if (isLand) {
            return [
                {
                    id: 'image',
                    header: 'IMAGES',
                    accessor: 'photos',
                    cell: (_, row) => renderImageCell(row)
                },
                {
                    id: 'client',
                    header: 'CLIENT',
                    accessor: 'client',
                    cell: (value) => (
                        <div className="d-flex flex-column text-start">
                            <span className="fw-semibold text-dark text-nowrap">{value?.name}</span>
                            <span className="small text-muted" style={{ fontSize: '11px' }}>{value?.mobile_number || "—"}</span>
                            <span className="small text-muted" style={{ fontSize: '11px' }}>{value?.email || "—"}</span>
                        </div>
                    )
                },
                {
                    id: 'clientType',
                    header: 'CLIENT TYPE',
                    accessor: 'client_type',
                },
                {
                    id: 'transactionType',
                    header: 'TRANSACTION TYPE',
                    accessor: 'transaction_type',
                },
                {
                    id: 'landType',
                    header: 'LAND TYPE',
                    accessor: 'land_type',
                    cell: (v) => <span className="fw-semibold text-dark">{v || "—"}</span>
                },
                {
                    id: 'tenure',
                    header: 'TENURE',
                    accessor: 'tenure',
                },
                {
                    id: 'location',
                    header: 'LOCATION',
                    cell: (_, row) => {
                        const locationText = [row.village?.name, row.taluka?.name, row.district?.name, row.state?.name]
                            .filter(Boolean)
                            .join(", ");
                        return <span className="small text-muted">{locationText || "—"}</span>;
                    }
                },
                {
                    id: 'landArea',
                    header: 'LAND AREA',
                    accessor: "land_area",

                },
                {
                    id: 'landAreaUnit',
                    header: 'LAND AREA UNIT',
                    accessor: 'land_area_unit'
                },
                {
                    id: 'pricePerUnit',
                    header: 'PRICE PER UNIT',
                    accessor: "price_per_unit",
                },
                {
                    id: 'priceUnit',
                    header: 'PRICE UNIT',
                    accessor: "price_unit"
                },
                {
                    id: 'totalPrice',
                    header: 'TOTAL PRICE',
                    accessor: "total_price"
                },
                {
                    id: 'status',
                    header: 'STATUS',
                    accessor: 'status',
                    cell: (v) => {

                        return v ? (
                            <StatusChip
                                bg={
                                    v === "Close"
                                        ? "warning"
                                        : v === "Live"
                                            ? "success"
                                            : v === "Open"
                                                ? "blue"
                                                : v === "Expired"
                                                    ? "danger"
                                                    : ""
                                }
                                pill
                                title={v}
                                className="text-capitalize"
                            />
                        ) : "—";
                    }
                },
                {
                    id: 'isArchived',
                    header: 'IS ARCHIVED',
                    accessor: "is_archived",
                    cell: (v) => (
                        <StatusChip
                            bg={v ? "danger" : "success"}
                            pill
                            title={v ? "Yes" : "No"}
                        />
                    )
                },
                {
                    id: 'linkedToPrivate',
                    header: 'LINKED TO PRIVATE',
                    accessor: "property_id",
                    cell: (v) => (
                        <StatusChip
                            bg={v ? "success" : "danger"}
                            pill
                            title={v ? "Yes" : "No"}
                        />
                    )
                },
                {
                    id: 'createdAt',
                    header: 'CREATED AT',
                    accessor: "created_at",
                    cell: (v) => formatMomentDate(v, "DD-MM-YYYY hh:mm A")
                },
                {
                    id: 'updatedAt',
                    header: 'UPDATED AT',
                    accessor: "updated_at",
                    cell: (v) => formatMomentDate(v, "DD-MM-YYYY hh:mm A")
                }
            ];
        }

        // Columns for properties (isLand === false)
        return [
            {
                id: 'image',
                header: 'IMAGES',
                accessor: 'files',
                cell: (_, row) => renderImageCell(row)
            },
            {
                id: 'client',
                header: 'CLIENT',
                accessor: 'client',
                cell: (v) => (
                    <div className="d-flex flex-column text-start">
                        <span className="fw-semibold text-dark text-nowrap">{v?.name || "—"}</span>
                        <span className="small text-muted" style={{ fontSize: '11px' }}>{v?.mobile_number || "—"}</span>
                        <span className="small text-muted" style={{ fontSize: '11px' }}>{v?.email || "—"}</span>
                    </div>
                )
            },
            {
                id: 'clientType',
                header: 'CLIENT TYPE',
                accessor: 'client_type'
            },
            {
                id: 'transactionType',
                header: 'TRANSACTION TYPE',
                accessor: 'transaction_type'
            },
            {
                id: 'propertyType',
                header: 'PROPERTY TYPE',
                accessor: 'property_type',
                cell: (v) => <span className="fw-semibold text-dark">{v || "—"}</span>
            },
            {
                id: 'location',
                header: 'LOCATION',
                cell: (_, row) => {
                    const locationText = [row.locality?.name, row.city?.name, row.state?.name]
                        .filter(Boolean)
                        .join(", ");
                    return <span className="small text-muted">{locationText || "—"}</span>;
                }
            },
            {
                id: 'sBuiltupArea',
                header: 'S. BUILTUP AREA',
                accessor: "s_builtup_area"
            },
            {
                id: 'sBuiltupAreaUnit',
                header: 'S. BUILTUP AREA UNIT',
                accessor: "s_builtup_area_unit"
            },
            {
                id: 'plotArea',
                header: 'PLOT AREA',
                accessor: "plot_area"
            },
            {
                id: 'plotAreaUnit',
                header: 'PLOT AREA UNIT',
                accessor: "plot_area_unit"
            },
            {
                id: 'possession',
                header: 'POSSESSION',
                accessor: "possession"
            },
            {
                id: 'furnishingStatus',
                header: 'FURNISHING STATUS',
                accessor: "furnishing_status"
            },
            {
                id: 'pricePerUnit',
                header: 'PRICE PER UNIT',
                accessor: 'price_per_unit'
            },
            {
                id: 'priceUnit',
                header: 'PRICE UNIT',
                accessor: 'price_unit'
            },
            {
                id: 'totalPrice',
                header: 'TOTAL PRICE',
                accessor: 'total_price'
            },
            {
                id: 'status',
                header: 'STATUS',
                accessor: 'status',
                cell: (v) => {
                    return v ? (
                        <StatusChip
                            bg={
                                v === "Close"
                                    ? "warning"
                                    : v === "Live"
                                        ? "success"
                                        : v === "Open"
                                            ? "blue"
                                            : v === "Expired"
                                                ? "danger"
                                                : ""
                            }
                            pill
                            title={v}
                            className="text-capitalize"
                        />
                    ) : "—";
                }
            },
            {
                id: 'isArchived',
                header: 'IS ARCHIVED',
                accessor: "is_archived",
                cell: (v) => (
                    <StatusChip
                        bg={v ? "danger" : "success"}
                        pill
                        title={v ? "Yes" : "No"}
                    />
                )
            },
            {
                id: 'linkedToPrivate',
                header: 'LINKED TO PRIVATE',
                accessor: "property_id",
                cell: (v) => (
                    <StatusChip
                        bg={v ? "success" : "danger"}
                        pill
                        title={v ? "Yes" : "No"}
                    />
                )
            },
            {
                id: 'createdAt',
                header: 'CREATED AT',
                cell: (_, row) => formatMomentDate(row.created_at, "DD-MM-YYYY hh:mm A")
            },
            {
                id: 'updatedAt',
                header: 'UPDATED AT',
                cell: (_, row) => formatMomentDate(row.updated_at, "DD-MM-YYYY hh:mm A")
            }
        ];
    }, [isLand, renderImageCell]);

    const totalPages = Math.ceil(total / pageSize);
    const activePage = Math.min(currentPage, Math.max(1, totalPages));

    return (
        <div className="bg-white rounded-2 overflow-hidden common-shadow p-4">
            <div className="d-flex align-items-center justify-content-between gap-4 mb-4">
                <TableSearch
                    value={searchTerm}
                    placeholder="Search"
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                    }}
                    onClear={() => {
                        setSearchTerm('');
                        setCurrentPage(1);
                    }}
                />
                <CommonButton
                    icon={<Plus size={16} />}
                    variant="primary"
                    title={isLand ? "Add Public Land" : "Add Public Property"}
                    type="button"
                    onClick={() => {
                        navigate(isLand ? routes.addPublicLand : routes.addPublicProperty, {
                            state: {
                                isPublicProperty: true,
                                item: { user_id: id, user: userDetail },
                                fromUserDetail: true,
                                isLand: isLand
                            }
                        });
                    }}
                />
            </div>

            <div className="overflow-hidden">
                <CustomTable
                    data={propertiesList}
                    columns={columns}
                    keyField="id"
                    actionList={actionList}
                    emptyMessage={`No ${isLand ? 'lands' : 'properties'} found`}
                    loading={loading}
                    rowClick={handleViewProperty}
                    tbody={{ trClass: "cursor-pointer" }}
                    cover={{ mainClass: "user-properties-table" }}
                    pagination={{
                        currentPage: activePage,
                        totalPages,
                        total,
                        onPageChange: (page) => setCurrentPage(page),
                        pageSize,
                        setPageSize: (size) => {
                            setPageSize(size);
                            setCurrentPage(1);
                        },
                        setCurrentPage,
                    }}
                />
            </div>
            {renderPreviewModal()}
            <ConfirmModal
                isOpen={confirmModalConfig.isOpen}
                onClose={() => setConfirmModalConfig((prev) => ({ ...prev, isOpen: false }))}
                title={confirmModalConfig.title}
                message={confirmModalConfig.message}
                onConfirm={confirmModalConfig.onConfirm}
                confirmText={confirmModalConfig.confirmText}
                cancelText={confirmModalConfig.cancelText}
                className="confirm-modal"
                size="sm"
            />
        </div>
    );
};

export default PublicProperties;
